import { useEffect, useMemo, useRef, useState } from "react";

import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import pickJevPageSuggestions from "~/helpers/search/pickJevPageSuggestions";
import useDebounce from "~/hooks/useDebounce";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import { type PageCandidate, type PageSearchMatch, suggestPageMatches } from "~/services/aiOrchestrator";

const FEW_BUILT_IN_RESULTS = 3;
const PAUSE_MS = 500;

interface UseJevPageSuggestionsArgs {
	pages: PageCandidate[];
	search: string;
	shownPageIds: readonly string[];
	skip: boolean;
}

interface JevAnswer {
	matches: PageSearchMatch[];
	text: string;
}

const useJevPageSuggestions = ({ pages, search, shownPageIds, skip }: UseJevPageSuggestionsArgs): PageSearchMatch[] => {
	const { isEnabled } = useFeatureFlag(FeatureFlagName.JevDecisions);
	const text = search.trim();
	const pausedText = useDebounce(text, PAUSE_MS);
	const askedTextRef = useRef("");
	const [answer, setAnswer] = useState<JevAnswer | null>(null);
	const canSuggest = isEnabled && !skip && shownPageIds.length < FEW_BUILT_IN_RESULTS;
	const shouldAsk = canSuggest && text !== "" && pausedText === text;

	useEffect(() => {
		if (!shouldAsk || askedTextRef.current === text) return;
		askedTextRef.current = text;
		void suggestPageMatches(text, pages).then((matches) => {
			if (askedTextRef.current === text) setAnswer({ matches, text });
		});
	}, [shouldAsk, text]);

	return useMemo(
		() => (canSuggest && answer?.text === text ? pickJevPageSuggestions(answer.matches, shownPageIds) : []),
		[answer, canSuggest, shownPageIds, text]
	);
};

export default useJevPageSuggestions;
